// Opens the (read-only) Skipper Chat and hands the guided-reports chat its opening turn.
// We cannot add an imperative open API to lib/skipper (out of scope), so we click
// the chat button's fixed-id DOM root to toggle the sidebar, then drive the
// assistant singleton's existing public API. The entry states what the user asked
// for and, when they opened one, which report they opened.
import type { ChatAssistant } from '@aytm/lib/skipper/types'
import type { GuidedReportEntry } from '@aytm/lib/skipper/assistants/common/types'
import {
  CHAT_BUTTON_ROOT_ID,
  CHAT_SIDEBAR_OPEN_CLASS,
  CREATE_REPORT_INTENT,
  MODIFY_REPORT_INTENT,
  SEED_CREATE_NEW,
  SEED_CHANGE_REPORT
} from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReportsConst'

type StatsAssistant = Pick<ChatAssistant, 'id' | 'ready' | 'inputReady' | 'init' | 'currentPrompt' | 'send'> & {
  initFailed?: boolean
  setGuidedReportEntry(entry: GuidedReportEntry | null): void
  seedPrompt(prompt: string): void
}

// Keyed on the affordance the user pressed rather than on the intent it carries: the two differ on
// the popup's entry, which arms `create_report` on a row that has no report id yet. `report: null`
// is that case.
type EntryOptions = { entry: 'create' } | { entry: 'change'; report: { name: string; id: number } | null }

const getAssistant = (): StatsAssistant | null =>
  (window.$STATS?.skipperChatAssistants?.assistants?.find(a => 'stats' === a.id) as unknown as
    | StatsAssistant
    | undefined) ?? null

const openSidebar = (): void => {
  if (document.body.classList.contains(CHAT_SIDEBAR_OPEN_CLASS)) return
  const root = document.querySelector(`#${CHAT_BUTTON_ROOT_ID}`)
  root?.querySelector('button')?.click()
}

// A failed initialisation still marks the chat settled, so readiness alone reads a dead chat as a
// usable one.
const isDown = (assistant: StatsAssistant): boolean => !assistant.ready || true === assistant.initFailed

// The input is held closed for as long as the turn is running, the assistant's reply included.
const isBusy = (assistant: StatsAssistant): boolean => !isDown(assistant) && !assistant.inputReady

export const openGuidedReportsChat = async (options: EntryOptions): Promise<void> => {
  const assistant = getAssistant()
  if (!assistant) return

  openSidebar()

  const target = 'change' === options.entry ? options.report : null
  const seed = target ? SEED_CHANGE_REPORT(target.name, target.id) : SEED_CREATE_NEW
  // The id rides structurally beside the intent: read off the seed's text it would only reach the
  // agent, leaving the host to resolve a basis of its own and restore another report's setup.
  const entry: GuidedReportEntry = target
    ? { intent: MODIFY_REPORT_INTENT, report_id: target.id }
    : { intent: CREATE_REPORT_INTENT }

  if (isDown(assistant) && assistant.init) {
    await assistant.init()
  }

  // A second click while the turn the first one started is still running brings the chat into view
  // and spends nothing — no second turn, and the same request is not answered twice.
  if (isBusy(assistant)) return

  const canArm = 'function' == typeof assistant.setGuidedReportEntry
  // The entry signal rides the opening turn, so it is armed before the send, always.
  if (canArm) assistant.setGuidedReportEntry(entry)

  // An untagged turn cannot tell the assistant whether it is being asked to create or to change,
  // and a chat that is still down cannot take one at all. Either way the seed goes to the composer
  // unsent — the user keeps their request in front of them, with the failure already on screen.
  if (!canArm || isDown(assistant)) {
    assistant.seedPrompt(seed)
    return
  }

  await assistant.send(seed)
}
